import {FC, useMemo} from "react";
import {Route, Switch} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useFlags} from "launchdarkly-react-client-sdk";
import {LoginCallback, SecureRoute} from "@okta/okta-react";
import Logout from "@common-core/okta-logout";
import {useEnvironment} from "@common-core/react-runtime/context";
import {Environment} from "./runtime";
import {useSidebarItems} from "./navigation";
import {ActivityIndicator} from "./activity-indicator";
import {
  AuthorizationCheck,
  Authorized,
  MANAGE_SIMPLE_OPERATIONAL_HIERARCHY,
  Unauthorized
} from "./access";
import {ErrorPage} from "./layout";
import {ProfilePage} from "./profile";

import {OperationalEntityPage} from "./entities/operational-hierarchy";
import {CreateOrganizationPage} from "./entities/operational-hierarchy/create-organization";
import {CreateHierarchyPage} from "./entities/operational-hierarchy/create-hierarchy";
import {ClientHierarchyPage} from "./entities/client-hierarchy";
import {ClientHierarchySearchPage} from "./entities/client-hierarchy/search";
import {OperationalSearchPage} from "./entities/operational-hierarchy/search";
import {OperationalHomePage} from "./home/operational-hierarchy";
import {CreateNewEntityPage} from "./entities/operational-hierarchy/create-new-entity";
import {SidebarComponent, SidebarComponentPage} from "./pbc";
import {useItemIsVisible} from "./visibility";

/**
 * The {@link Routes} component is responsible for routing
 * requests within the application to the appropriate component.
 *
 * @constructor
 */
export const Routes: FC = () => {
  const {t} = useTranslation();
  const flags = useFlags();
  const sidebarItems = useSidebarItems();
  const environment = useEnvironment<Environment>();
  const isVisible = useItemIsVisible();

  const sidebarComponents = useMemo<SidebarComponent[]>(
    () => environment.sidebarComponents.filter(isVisible),
    [environment.sidebarComponents, isVisible]
  );

  return (
    <Switch>
      <Route path="/login/callback">
        <LoginCallback
          loadingElement={<ActivityIndicator />}
          errorComponent={ErrorPage}
        />
      </Route>
      <Route path="/logout/callback">
        <Logout />
      </Route>
      <SecureRoute path="/" exact={true} errorComponent={ErrorPage}>
        <OperationalHomePage />
      </SecureRoute>
      <SecureRoute path="/search" errorComponent={ErrorPage}>
        <OperationalSearchPage />
      </SecureRoute>
      <SecureRoute path="/entity/:id" errorComponent={ErrorPage}>
        <OperationalEntityPage />
      </SecureRoute>
      <SecureRoute path="/client-hierarchy-search" errorComponent={ErrorPage}>
        <ClientHierarchySearchPage />
      </SecureRoute>
      <SecureRoute path="/client-hierarchy/:id" errorComponent={ErrorPage}>
        <ClientHierarchyPage />
      </SecureRoute>
      <SecureRoute
        path="/create-operational-entity/:id"
        errorComponent={ErrorPage}>
        <AuthorizationCheck permissions={[MANAGE_SIMPLE_OPERATIONAL_HIERARCHY]}>
          <Authorized>
            <CreateOrganizationPage />
          </Authorized>
          <Unauthorized>
            <ErrorPage
              error={t("error-page.not-authorized", "???")}
              severity={"caution"}
              sidebarItems={sidebarItems}
              logo={false}
            />
          </Unauthorized>
        </AuthorizationCheck>
      </SecureRoute>
      <SecureRoute path="/create-organization/:id" errorComponent={ErrorPage}>
        <AuthorizationCheck permissions={[MANAGE_SIMPLE_OPERATIONAL_HIERARCHY]}>
          <Authorized>
            <CreateOrganizationPage />
          </Authorized>
          <Unauthorized>
            <ErrorPage
              error={t("error-page.not-authorized", "???")}
              severity={"caution"}
              sidebarItems={sidebarItems}
              logo={false}
            />
          </Unauthorized>
        </AuthorizationCheck>
      </SecureRoute>
      <SecureRoute
        path="/create-new-entity/"
        exact={true}
        errorComponent={ErrorPage}>
        <AuthorizationCheck permissions={[MANAGE_SIMPLE_OPERATIONAL_HIERARCHY]}>
          <Authorized>
            <CreateNewEntityPage />
          </Authorized>
          <Unauthorized>
            <ErrorPage
              error={t("error-page.not-authorized", "???")}
              severity={"caution"}
              sidebarItems={sidebarItems}
              logo={false}
            />
          </Unauthorized>
        </AuthorizationCheck>
      </SecureRoute>
      <SecureRoute
        path="/create-operational-hierarchy/:id/simple"
        exact={true}
        errorComponent={ErrorPage}>
        <AuthorizationCheck permissions={[MANAGE_SIMPLE_OPERATIONAL_HIERARCHY]}>
          <Authorized>
            <CreateHierarchyPage />
          </Authorized>
          <Unauthorized>
            <ErrorPage
              error={t("error-page.not-authorized", "???")}
              severity={"caution"}
              sidebarItems={sidebarItems}
              logo={false}
            />
          </Unauthorized>
        </AuthorizationCheck>
      </SecureRoute>
      <SecureRoute path="/profile" errorComponent={ErrorPage}>
        <ProfilePage />
      </SecureRoute>
      {sidebarComponents.map(component => (
        <SecureRoute
          key={component.id}
          path={component.path}
          exact={true}
          errorComponent={ErrorPage}>
          <SidebarComponentPage component={component} />
        </SecureRoute>
      ))}
      <Route path="/error-details">
        <ErrorPage
          error={window.error}
          severity={"error"}
          sidebarItems={sidebarItems}
          logo={false}
        />
        )
      </Route>
      <Route>
        <ErrorPage
          error={t("error-page.invalid-route", "???")}
          severity={"error"}
          sidebarItems={sidebarItems}
          logo={false}
        />
      </Route>
    </Switch>
  );
};
